import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { EventAttendeeTicketOrder } from '@models/events/event-attendee-ticket-order.model';
import { EventAttendeeTicketOrderRefundStatus } from '@models/events/event-attendee.ticket-order-refund-status.enum';
import { Event } from '@models/events/event.model';
import { User } from '@models/users/user.model';
import { ChangeSlotDialog } from '@modules/events/dialogs/change-slot/change-slot.dialog';
import { RequestRefundDialog } from '@modules/events/dialogs/request-refund/request-refund.dialog';
import { RequestedRefundOverviewDialog } from '@modules/events/dialogs/requested-refund-overview/requested-refund-overview.dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';

@Component({
  selector: 'app-ticket-order-card',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
  ],
  templateUrl: './ticket-order-card.component.html',
  styleUrl: './ticket-order-card.component.scss',
})
export class TicketOrderCardComponent {
  @Input() event?: Event;
  @Input() order!: EventAttendeeTicketOrder;
  @Input() headerView = false;
  @Input() loggedUser?: User;

  @Output() selectOrder = new EventEmitter<EventAttendeeTicketOrder>();
  @Output() onRequestRefund = new EventEmitter<boolean>();
  @Output() onClose = new EventEmitter<boolean>();

  currentLang?: string;

  constructor(
    private translateService: TranslateService,
    private dialog: MatDialog,
  ) {
    this.translateService.onLangChange
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.currentLang = this.translateService.currentLang;
      });
    this.currentLang = this.translateService.currentLang;
  }

  onSelectOrder() {
    if (this.headerView) {
      return;
    }

    this.selectOrder.emit(this.order);
  }

  hasRequestedRefund(): boolean {
    return !!this.order.refund;
  }

  getRefundProgressIcon() {
    if (!this.order || !this.order.refund) return '';

    switch (this.order.refund.status) {
      case EventAttendeeTicketOrderRefundStatus.SUCCEEDED:
        return `fa-circle-check`;
      case EventAttendeeTicketOrderRefundStatus.PENDING ||
        EventAttendeeTicketOrderRefundStatus.APPROVED_INITIATED:
        return `fa-hourglass-half`;
      default:
        return `fa-circle-xmark`;
    }
  }

  getRefundProgress() {
    if (!this.order || !this.order.refund) return '';

    switch (this.order.refund.status) {
      case EventAttendeeTicketOrderRefundStatus.SUCCEEDED:
        return `${this.translateService.instant(
          'APP.REQUEST_REFUND.REFUNDED',
        )}`;
      case EventAttendeeTicketOrderRefundStatus.PENDING ||
        EventAttendeeTicketOrderRefundStatus.APPROVED_INITIATED:
        return this.translateService.instant(
          'APP.REQUEST_REFUND.REFUND_IS_IN_PROCESS',
        );
      default:
        return this.translateService.instant(
          'APP.REQUEST_REFUND.REFUND_DENIED',
        );
    }
  }

  changeTimeslot() {
    const dialogRef = this.dialog.open(ChangeSlotDialog, {
      maxWidth: '602px',
      maxHeight: '100vh',
      width: '100%',
      height: 'auto',
      autoFocus: false,
      disableClose: true,
      data: {
        event: this.event,
        order: this.order,
        loggedUser: this.loggedUser,
        ticket: this.order.eventAttendeeTickets[0].eventTicket,
      },
      panelClass: ['normal-dialog'],
    });

    dialogRef.afterClosed().subscribe({
      next: (res) => {
        if (res.close) {
          this.onClose.emit(true);
        }
      },
    });
  }

  requestRefund() {
    if (this.order) {
      if (this.order.refund) {
        const dialogRef = this.dialog.open(RequestedRefundOverviewDialog, {
          maxWidth: '602px',
          maxHeight: '100vh',
          width: '100%',
          height: 'auto',
          data: {
            event: this.event,
            order: this.order,
            loggedUser: this.loggedUser,
          },
          panelClass: ['normal-dialog', 'overlay-theme'],
        });

        dialogRef.afterClosed().subscribe(() => {});
      } else {
        const dialogRef = this.dialog.open(RequestRefundDialog, {
          maxWidth: '602px',
          maxHeight: '100vh',
          width: '100%',
          height: 'auto',
          autoFocus: false,
          disableClose: true,
          data: {
            event: this.event,
            order: this.order,
            loggedUser: this.loggedUser,
          },
          panelClass: ['normal-dialog', 'new-normal-dialog'],
        });

        dialogRef.afterClosed().subscribe((isRefresh) => {
          if (isRefresh) {
            this.onRequestRefund.emit(true);
          }
        });
      }
    }
  }

  get changeSlotAvailable(): boolean {
    return !!this.order.eventSlot && !this.order.eventSlot.isInThePast();
  }

  get refundAvailable(): boolean {
    const refundAllowed = this.event?.isRefundAllowed();
    if (!refundAllowed) return false;

    const deadlineInDays =
      this.event?.ticketRefundPolicy?.deadlineForRefundsInDays;
    const baseDate = this.order.eventSlot?.startDate ?? this.event?.startDate;

    if (!baseDate) return true;

    const refundDeadline = deadlineInDays
      ? moment(baseDate).subtract(deadlineInDays, 'days')
      : moment(baseDate);

    return moment().isBefore(refundDeadline);
  }
}
